import { useEffect, useRef, useState } from 'react';
import { fetchAdminTournamentRanking } from '../infrastructure/adminApi';

const PAGE_SIZE = 50;
const buttonClass = 'min-h-[44px] border border-[#594a65] px-4 py-2 text-xs font-bold text-[#d6c9df] disabled:opacity-50';
const formatDate = (value) => value == null ? '—' : new Date(value).toLocaleString('es-MX', {
  dateStyle: 'medium', timeStyle: 'short',
});

export default function TournamentResultsPanel({ authorization, tournament, onSessionExpired, onClose }) {
  const headingRef = useRef(null);
  const [offset, setOffset] = useState(0);
  const [reload, setReload] = useState(0);
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError('');
    setResult(null);
    fetchAdminTournamentRanking(authorization, tournament.id, { limit: PAGE_SIZE, offset })
      .then((payload) => {
        if (!cancelled) setResult(payload);
      })
      .catch((requestError) => {
        if (cancelled) return;
        if (requestError.status === 401) onSessionExpired?.();
        else setError(requestError.message);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [authorization, tournament.id, offset, reload, onSessionExpired]);

  const current = result?.tournament || tournament;

  return (
    <section
      id="tournament-results"
      aria-labelledby="tournament-results-heading"
      aria-busy={isLoading}
      className="mt-6 border border-[#d8b6ee]/60 bg-[#0d0b11] p-5"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3
            id="tournament-results-heading"
            ref={headingRef}
            tabIndex={-1}
            className="break-words font-display text-2xl uppercase text-white"
          >
            Resultados: {current.name}
          </h3>
          <p className="mt-2 text-sm text-[#b7a9c4]">
            {current.status === 'closed' ? 'Torneo cerrado' : 'Torneo activo'}
            {result && ` · ${result.total} participantes con puntaje registrado`}
          </p>
          <p className="mt-2 text-xs leading-5 text-[#9d8cab]">
            Ordenados por puntos, precisión y fecha de registro. Cada resultado corresponde a una partida de este torneo.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={isLoading}
            onClick={() => setReload((value) => value + 1)}
            className={buttonClass}
          >
            Actualizar resultados
          </button>
          <button type="button" onClick={onClose} className={buttonClass}>
            Cerrar detalle
          </button>
        </div>
      </div>
      {isLoading && <p role="status" className="mt-4 text-sm text-[#b7a9c4]">Cargando participantes…</p>}
      {error && <p role="alert" className="mt-4 text-sm text-[#ffd8de]">{error}</p>}
      {result && (
        <>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[560px] text-left text-sm">
              <caption className="sr-only">Participantes y resultados de {current.name}</caption>
              <thead>
                <tr className="border-b border-[#312a3a] text-xs uppercase text-[#9d8cab]">
                  <th scope="col" className="px-3 py-3">Posición</th>
                  <th scope="col" className="px-3 py-3">Participante</th>
                  <th scope="col" className="px-3 py-3 text-right">Puntos</th>
                  <th scope="col" className="px-3 py-3 text-right">Precisión</th>
                  <th scope="col" className="px-3 py-3">Fecha de partida</th>
                </tr>
              </thead>
              <tbody>
                {result.entries.map((entry) => (
                  <tr key={entry.rank} className="border-b border-[#1d1826] text-[#eee8f5]">
                    <td className="px-3 py-3">{entry.rank}</td>
                    <th scope="row" className="px-3 py-3 font-bold">
                      @{entry.username}
                      {current.status === 'closed' && entry.rank === 1 && (
                        <span className="ml-2 text-xs text-[#F7E04B]">Ganador</span>
                      )}
                    </th>
                    <td className="px-3 py-3 text-right tabular-nums">{entry.score.toLocaleString('es-MX')}</td>
                    <td className="px-3 py-3 text-right tabular-nums">
                      {entry.accuracy == null ? '—' : `${entry.accuracy.toLocaleString('es-MX', {
                        minimumFractionDigits: 1, maximumFractionDigits: 1,
                      })}%`}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-xs">{formatDate(entry.playedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {result.total === 0 && (
            <p className="py-6 text-center text-sm text-[#9d8cab]">Este torneo todavía no tiene puntajes registrados.</p>
          )}
          {result.total > 0 && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <p role="status" className="text-xs text-[#b7a9c4]">
                Mostrando {result.entries.length ? offset + 1 : 0}–{offset + result.entries.length} de {result.total}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={offset === 0 || isLoading}
                  onClick={() => setOffset((value) => Math.max(0, value - PAGE_SIZE))}
                  className={buttonClass}
                >
                  Anterior
                </button>
                <button
                  type="button"
                  disabled={offset + PAGE_SIZE >= result.total || isLoading}
                  onClick={() => setOffset((value) => value + PAGE_SIZE)}
                  className={buttonClass}
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
